import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Mail, Phone, Star, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { HERO_IMAGE } from "@/lib/data";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS_FALLBACK = [
  { value: "100", label: "Totes in rotation" },
  { value: "4 hr", label: "Delivery windows" },
  { value: "4.9", label: "Member rating", icon: true },
  { value: "0", label: "Cardboard waste" },
];

export const Hero = () => {
  const navigate = useNavigate();
  const [totesInRotation, setTotesInRotation] = useState(null);

  useEffect(() => {
    api.get("/site-stats").then((r) => setTotesInRotation(r.data.totes_in_rotation)).catch(() => {});
  }, []);

  const STATS = STATS_FALLBACK.map((s, i) =>
    i === 0 && totesInRotation != null ? { ...s, value: String(totesInRotation) } : s);
  const [zip, setZip] = useState("");
  const [zipResult, setZipResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const outlineX = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const search = async (e) => {
    e.preventDefault();
    if (!/^\d{5}$/.test(zip.trim())) {
      setZipResult(null);
      return toast.error("Enter a 5-digit zip code");
    }
    setChecking(true);
    try {
      const { data } = await api.get(`/delivery-zone?zip=${zip.trim()}`);
      setZipResult(data);
      sessionStorage.setItem("otr_zip", zip.trim());
    } catch {
      toast.error("Couldn't check that zip — try again");
    } finally {
      setChecking(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={onTilt}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="grain relative overflow-hidden bg-paper"
    >
      <div className="absolute inset-0" aria-hidden>
        <img
          src="/brand/hero-landscape.jpg"
          alt=""
          className="h-full w-full object-cover object-center"
          data-testid="hero-background"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-paper/90 via-paper/35 to-paper/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-paper/25 via-transparent to-paper" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-14 sm:px-8 sm:pt-20 lg:px-16">
        <div className="grid items-center gap-14 pb-8 lg:grid-cols-12 lg:pb-0">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="mb-7 flex flex-wrap items-center gap-3"
              data-testid="hero-contact-strip"
            >
              <a
                href="tel:+15414087019"
                data-testid="hero-call-link"
                className="flex items-center gap-2.5 rounded-full bg-royal px-5 py-3 text-sm font-bold text-white shadow-lg shadow-royal/30 transition-all duration-200 hover:scale-[1.03] hover:bg-royal-dark"
              >
                <Phone className="h-4 w-4" /> Call: (541) 408-7019
              </a>
              <a
                href="mailto:reservations@oregon-tote-rentals.com"
                data-testid="hero-email-link"
                className="flex items-center gap-2.5 rounded-full border border-slate-200 bg-white/95 px-5 py-3 text-sm font-bold text-ink shadow-md shadow-slate-900/5 backdrop-blur transition-all duration-200 hover:scale-[1.03] hover:border-royal hover:text-royal"
              >
                <Mail className="h-4 w-4" /> Email: reservations@oregon-tote-rentals.com
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-royal"
            >
              Oregon Tote Rentals — Bend, OR
            </motion.p>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>Skip the box.</MaskedLine>
              <MaskedLine delay={0.32}>
                Rent the{" "}
                <span className="font-serif font-normal italic text-royal">tote.</span>
              </MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-6 max-w-lg text-base leading-relaxed text-slate-600"
            >
              Reusable moving totes, bins and dollies — sanitized, stacked
              and delivered to your door, then picked up when you're unpacked.
              Free delivery and pickup across Bend, Redmond, Sisters and Sunriver.
            </motion.p>
            <motion.form
              onSubmit={search}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-9 flex max-w-xl flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-xl shadow-slate-900/5 sm:flex-row sm:items-center"
            >
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                data-testid="hero-zip-input"
                placeholder="Zip code"
                inputMode="numeric"
                maxLength={5}
                className="h-12 w-full flex-1 rounded-xl border border-slate-200 bg-paper px-4 text-sm font-medium text-ink outline-none transition-colors duration-200 focus:border-royal"
              />
              <button
                type="submit"
                disabled={checking}
                data-testid="hero-zip-submit"
                className="flex h-12 items-center justify-center gap-2 rounded-xl bg-royal px-6 text-sm font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:bg-royal-dark hover:shadow-lg hover:shadow-royal/25 active:scale-[0.98] disabled:opacity-60"
              >
                {checking ? "Checking…" : "Check my zip"} <ArrowRight className="h-4 w-4" />
              </button>
            </motion.form>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-3 flex max-w-xl items-center gap-2 pl-1 text-xs font-medium text-slate-500"
              data-testid="hero-delivery-fee-note"
            >
              <Truck className="h-3.5 w-3.5 shrink-0 text-royal" />
              Delivery fees $1.33 per mile outside the free delivery areas.
            </motion.p>
            <AnimatePresence>
              {zipResult && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  data-testid="hero-zip-result"
                  className={`mt-4 flex max-w-xl flex-wrap items-center gap-3 rounded-2xl border p-4 ${
                    zipResult.eligible
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-amber-200 bg-amber-50"
                  }`}
                >
                  {zipResult.eligible ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                      <p className="text-sm font-semibold text-emerald-800">
                        {zipResult.area
                          ? `${zipResult.area} — you're in the free delivery & pickup zone.`
                          : `Good news — ${zipResult.zip} is inside our 25-mile free delivery radius.`}
                      </p>
                      <button
                        onClick={() => navigate("/catalog")}
                        data-testid="hero-zip-browse"
                        className="ml-auto flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
                      >
                        Browse packages <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : zipResult.delivery_fee != null ? (
                    <>
                      <XCircle className="h-5 w-5 shrink-0 text-amber-600" />
                      <p className="text-sm font-semibold text-amber-800" data-testid="hero-zip-fee-quote">
                        {zipResult.zip} is about {Math.round(zipResult.distance_miles)} miles out — delivery & pickup fee:{" "}
                        <span className="font-extrabold">${zipResult.delivery_fee.toFixed(2)}</span>{" "}
                        ($1.33/mile round trip beyond our free zone). Email reservations@oregon-tote-rentals.com to book.
                      </p>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 shrink-0 text-amber-600" />
                      <p className="text-sm font-semibold text-amber-800">
                        We don't recognize that zip — email reservations@oregon-tote-rentals.com and we'll quote you.
                      </p>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-4"
            >
              {STATS.map((s) => (
                <div key={s.label} className="border-l-2 border-royal/20 pl-4">
                  <p className="flex items-center gap-1.5 font-display text-2xl font-bold text-ink">
                    {s.value}
                    {s.icon && <Star className="h-4 w-4 fill-gold text-gold" />}
                  </p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    {s.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>
          <div className="lg:col-span-5" style={{ perspective: 1200 }}>
            <motion.div
              style={{ rotateX, rotateY, y: visualY, transformStyle: "preserve-3d" }}
              className="relative"
              data-testid="hero-visual"
            >
              <div className="absolute -inset-3 rounded-[2rem] border border-royal/25" aria-hidden />
              <motion.div
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{ clipPath: "inset(0 0 0% 0)" }}
                transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-[1.75rem] shadow-2xl shadow-slate-900/20"
              >
                <img
                  src={HERO_IMAGE}
                  alt="Oregon Tote Rentals driver delivering stacked blue totes to a customer's doorstep"
                  className="h-[380px] w-full object-cover sm:h-[440px]"
                />
              </motion.div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                style={{ transform: "translateZ(50px)" }}
                className="absolute bottom-4 left-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
              >
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-royal-light">
                  <Truck className="h-5 w-5 text-royal" />
                </span>
                <div>
                  <p className="text-xs font-semibold text-ink">Delivered to your door</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                    Free pickup included
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
        <motion.div
          aria-hidden
          style={{ x: outlineX }}
          className="text-outline pointer-events-none mt-20 select-none whitespace-nowrap font-display text-[8vw] font-extrabold leading-[0.9]"
        >
          Oregon Tote Rentals
        </motion.div>
      </div>
    </section>
  );
};
